import type { Dev } from '../../types/domain';
import { addDays, dateFromIso, isoStr } from '../dates';
import type { MergedSegment, PlacedSegment, TimelineItem } from './types';

// Résout couleur/nom d'un dev à partir de son id — couleur personnalisée si définie, sinon une
// couleur de la palette assignée par position dans la liste (index modulo la taille de la palette).
// Partagé par la Timeline et la Facturation, qui n'utilisent qu'une palette et un texte de repli différents.
// Fusionne des segments contigus de même clé de regroupement (ex: brique+h/jour pour la Timeline
// par dev, dev+brique+h/jour par projet) : deux affectations qui se suivent sans trou redeviennent
// un seul segment visuel. Partagé par les deux vues de la Timeline, qui ne diffèrent que par la clé
// de regroupement et les champs portés par chaque item brut (chaque item doit porter au minimum
// eIdx/realStart/realEnd/totalH/affId).
export function mergeContiguousSegments<T extends TimelineItem>(
  rawItems: T[],
  groupKeyFn: (item: T) => string,
): MergedSegment<T>[] {
  const groups: Record<string, T[]> = {};
  rawItems.forEach((r) => {
    const k = groupKeyFn(r);
    (groups[k] = groups[k] || []).push(r);
  });
  const merged: MergedSegment<T>[] = [];
  Object.values(groups).forEach((list) => {
    list.sort((a, b) => a.realStart.localeCompare(b.realStart));
    let cur: MergedSegment<T> | null = null;
    for (const r of list) {
      const adjIso = cur ? isoStr(addDays(dateFromIso(cur.realEnd), 1)) : null;
      if (cur && adjIso && r.realStart <= adjIso) {
        cur.eIdx = Math.max(cur.eIdx, r.eIdx);
        cur.realEnd = r.realEnd > cur.realEnd ? r.realEnd : cur.realEnd;
        cur.totalH = Math.round((cur.totalH + r.totalH) * 10) / 10;
        cur.affIds.push(r.affId);
        cur.rightAffId = r.affId;
      } else {
        if (cur) merged.push(cur);
        cur = { ...r, affIds: [r.affId], leftAffId: r.affId, rightAffId: r.affId };
      }
    }
    if (cur) merged.push(cur);
  });
  return merged;
}

// Empilement glouton sans chevauchement : chaque segment prend la première lane déjà libre à sa
// date de début, sinon en ouvre une nouvelle. Partagé par les deux vues de la Timeline.
export function packLanes<S extends PlacedSegment>(segs: S[]): { segs: S[]; nLanes: number } {
  segs.sort((x, y) => x.sIdx - y.sIdx || x.eIdx - y.eIdx);
  const laneEnds: number[] = [];
  segs.forEach((sg) => {
    let lane = laneEnds.findIndex((end) => end < sg.sIdx);
    if (lane === -1) {
      lane = laneEnds.length;
      laneEnds.push(sg.eIdx);
    } else laneEnds[lane] = sg.eIdx;
    sg.lane = lane;
  });
  return { segs, nLanes: Math.max(1, laneEnds.length) };
}

export function devColorNameLookup(
  devs: Dev[] | null | undefined,
  palette: string[],
  fallbackName: string,
): { colorOf(devId: string): string; nameOf(devId: string): string } {
  return {
    colorOf(devId: string) {
      const dev = (devs || []).find((d) => d.id === devId);
      if (dev?.color) return dev.color;
      const i = (devs || []).findIndex((d) => d.id === devId);
      return palette[i >= 0 ? i % palette.length : 0];
    },
    nameOf(devId: string) {
      return (devs || []).find((d) => d.id === devId)?.nom || fallbackName;
    },
  };
}
